import { BaseService } from './BaseService';
import { API_ENDPOINTS } from './config';
import { Feature, FeatureCreate, FeatureUpdate } from '../../types/subscription';
import api from './api';

export class FeatureService extends BaseService {
  /**
   * Transform backend feature response to frontend Feature interface
   * Maps feature_code -> code, feature_name -> name, feature_category -> category
   */
  private static transformFeature(data: any): Feature {
    return {
      id: data.id || data.feature_id,
      code: data.code || data.feature_code || '',
      name: data.name || data.feature_name || '',
      description: data.description,
      category: data.category || data.feature_category,
      is_core_feature: data.is_core_feature,
      metadata: data.metadata,
    };
  }

  /**
   * Transform array of backend feature responses
   */
  private static transformFeatures(data: any[]): Feature[] {
    return data.map(item => this.transformFeature(item));
  }

  /**
   * Get all features
   */
  static async getAll(): Promise<Feature[]> {
    try {
      const response = await this.get<any[]>(API_ENDPOINTS.FEATURES.BASE);
      return this.transformFeatures(response);
    } catch (error) {
      console.error('[FeatureService] Error fetching features:', error);
      return [];
    }
  }

  /**
   * Get feature by ID
   */
  static async getById(id: string): Promise<Feature | null> {
    try {
      const response = await this.get<any>(API_ENDPOINTS.FEATURES.BY_ID(id));
      console.log('[FeatureService.getById] Raw backend response:', {
        id,
        response,
        has_metadata: !!response.metadata,
        metadata: response.metadata,
        metadata_type: typeof response.metadata,
      });
      const transformed = this.transformFeature(response);
      console.log('[FeatureService.getById] Transformed feature:', {
        id: transformed.id,
        code: transformed.code,
        has_metadata: !!transformed.metadata,
        metadata: transformed.metadata,
        metadata_type: typeof transformed.metadata,
      });
      return transformed;
    } catch (error) {
      console.error('[FeatureService] Error fetching feature:', error);
      return null;
    }
  }

  /**
   * Get feature by code
   */
  static async getByCode(code: string): Promise<Feature | null> {
    try {
      const response = await this.get<any>(API_ENDPOINTS.FEATURES.BY_CODE(code));
      return this.transformFeature(response);
    } catch (error) {
      console.error('[FeatureService] Error fetching feature by code:', error);
      return null;
    }
  }

  /**
   * Create a new feature
   */
  static async create(data: FeatureCreate): Promise<Feature> {
    const response = await this.post<any>(
      API_ENDPOINTS.FEATURES.CREATE,
      data as unknown as Record<string, unknown>
    );
    return this.transformFeature(response);
  }

  /**
   * Create feature with optimistic check on 500 error
   * If creation returns 500, checks if feature was actually created by code
   */
  static async createWithOptimisticCheck(data: FeatureCreate): Promise<Feature> {
    try {
      return await this.create(data);
    } catch (error: any) {
      // Only handle 500 errors for optimistic check
      if (error.response?.status === 500 && data.feature_code) {
        console.log('[FeatureService] 500 error on create, checking if feature was actually created...');
        try {
          const existing = await this.getByCode(data.feature_code);
          if (existing) {
            console.log('[FeatureService] Feature was actually created despite 500 error:', existing.id);
            return existing;
          }
        } catch (checkError) {
          console.error('[FeatureService] Error checking for existing feature:', checkError);
        }
      }
      // Re-throw if not 500, or if optimistic check failed
      throw error;
    }
  }

  /**
   * Update feature
   * Note: Backend currently expects FeatureCreate model with required fields
   * (feature_code, feature_name, feature_category) instead of partial FeatureUpdate
   */
  static async update(id: string, data: FeatureUpdate | FeatureCreate): Promise<Feature> {
    const response = await this.put<any>(
      API_ENDPOINTS.FEATURES.UPDATE(id),
      data as unknown as Record<string, unknown>
    );
    console.log('[FeatureService.update] Raw backend response:', {
      response,
      has_metadata: !!response.metadata,
      metadata: response.metadata,
      metadata_type: typeof response.metadata,
    });
    const transformed = this.transformFeature(response);
    console.log('[FeatureService.update] Transformed feature:', {
      id: transformed.id,
      code: transformed.code,
      has_metadata: !!transformed.metadata,
      metadata: transformed.metadata,
      metadata_type: typeof transformed.metadata,
    });
    return transformed;
  }

  /**
   * Delete feature
   */
  static async delete(id: string): Promise<void> {
    try {
      await api.delete(API_ENDPOINTS.FEATURES.DELETE(id));
    } catch (error: any) {
      const isCorsError = error.code === 'ERR_NETWORK' || 
                         error.message?.includes('CORS') ||
                         error.message?.includes('Access-Control-Allow-Origin');
      if (!isCorsError) {
        console.error(`DELETE request failed for ${API_ENDPOINTS.FEATURES.DELETE(id)}:`, error);
      }
      throw error;
    }
  }
}

